import { PagingParameters } from "@/api/dtos";
import { DashboardListViewContext } from "@/context/dashboard-list-view/DashboardListViewContext";
import { PaginationToolbarContext } from "@/context/PaginationToolbarContext";
import { useDashboardListScrollRestore } from "@/hooks/useDashboardListScrollRestore";
import {
  DashboardListView,
  peekDashboardListScroll,
  readDashboardListViewPreference,
} from "@/lib/dashboardListScrollRestore";
import { LayoutGrid, Table2 } from "lucide-react";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import {
  DatatablePagination,
  DatatablePaginationProps,
} from "@/components/ui/datatable-pagination";
import { persistBoPreference } from "@/lib/user-preferences/store";

interface DashboardListGridLayoutProps {
  listTable: ReactNode;
  gridContent: ReactNode;
  pagination: DatatablePaginationProps;
  storageKey?: string;
  listKey?: string;
  entityIds?: string[];
  isLoading?: boolean;
  listPagination?: PagingParameters;
  setListPagination?: Dispatch<SetStateAction<PagingParameters>>;
  /** Default view when no user preference is stored yet. */
  defaultView?: DashboardListView;
}

export function DashboardListGridLayout({
  listTable,
  gridContent,
  pagination,
  storageKey,
  listKey,
  entityIds = [],
  isLoading = false,
  listPagination,
  setListPagination,
  defaultView = "list",
}: DashboardListGridLayoutProps) {
  const viewStorageKey = useMemo(
    () => (storageKey ? `${storageKey}-view` : null),
    [storageKey],
  );
  const [view, setView] = useState<DashboardListView>(() => {
    if (listKey) {
      const pending = peekDashboardListScroll(listKey);
      if (pending) return pending.view;
    }
    if (!viewStorageKey) return defaultView;
    return readDashboardListViewPreference(viewStorageKey, defaultView);
  });

  const handleRestoreView = useCallback((restoredView: DashboardListView) => {
    setView(restoredView);
  }, []);

  useDashboardListScrollRestore({
    listKey: listKey ?? "",
    entityIds,
    isLoading: !listKey ? true : isLoading,
    pagination: listPagination ?? { page: 0, pageSize: 10 },
    setPagination: setListPagination ?? (() => undefined),
    onRestoreView: listKey ? handleRestoreView : undefined,
  });

  useEffect(() => {
    if (!viewStorageKey) return;
    try {
      persistBoPreference(viewStorageKey, view);
    } catch {
      // ignore preference persistence failures
    }
  }, [view, viewStorageKey]);

  const viewContextValue = useMemo(() => ({ view }), [view]);

  const viewToggle = (
    <TabsList className="grid w-fit grid-cols-2 h-9">
      <TabsTrigger value="grid" className="px-2">
        <LayoutGrid className="h-4 w-4" />
      </TabsTrigger>
      <TabsTrigger value="list" className="px-2">
        <Table2 className="h-4 w-4" />
      </TabsTrigger>
    </TabsList>
  );

  return (
    <DashboardListViewContext.Provider value={viewContextValue}>
      <Tabs
        value={view}
        onValueChange={(value) => setView(value as DashboardListView)}
        className="w-full"
      >
        <TabsContent value="list">
          <PaginationToolbarContext.Provider value={viewToggle}>
            {listTable}
          </PaginationToolbarContext.Provider>
        </TabsContent>
        <TabsContent value="grid">
          <DatatablePagination {...pagination} endContent={viewToggle} />
          <div className="w-full flex gap-8 flex-wrap mt-2">{gridContent}</div>
          <div className="w-full my-2">
            <DatatablePagination {...pagination} />
          </div>
        </TabsContent>
      </Tabs>
    </DashboardListViewContext.Provider>
  );
}
